// Finding a place by name, for the host's Event Library and itinerary.
//
// The search itself is free: OpenStreetMap data, through Photon (built for
// search-as-you-type) with Nominatim as the fallback. This file holds the
// pure parts: turning either service's answer into one shape, a short
// readable address, and a first guess at the app's category. The functions
// place-search and place-suggest do the network calls.

export interface FoundPlace {
  /** Stable id from OpenStreetMap, e.g. "N123456" (node), "W…" (way), "R…" (relation). */
  id: string;
  name: string;
  street: string | null;
  number: string | null;
  postcode: string | null;
  city: string | null;
  country: string | null;
  lat: number;
  lng: number;
  /** Plain label of what it is: "Café", "Restaurant", "Museum"… */
  kind: string | null;
  /** Best guess at one of the app's category slugs, when the kind is clear. */
  categoryHint: string | null;
}

/** What the function accepts as a search: 3 to 120 characters once trimmed. */
export function cleanQuery(q: unknown): string | null {
  if (typeof q !== 'string') return null;
  const s = q.replace(/\s+/g, ' ').trim();
  return s.length >= 3 && s.length <= 120 ? s : null;
}

/** A point to bias results toward (the host's city), or null if it isn't a real coordinate pair. */
export function cleanNear(n: unknown): { lat: number; lng: number } | null {
  const o = n as { lat?: unknown; lng?: unknown } | null;
  if (!o || typeof o.lat !== 'number' || typeof o.lng !== 'number') return null;
  if (!(o.lat >= -90 && o.lat <= 90 && o.lng >= -180 && o.lng <= 180)) return null;
  return { lat: o.lat, lng: o.lng };
}

const KIND_LABEL: Record<string, string> = {
  cafe: 'Café', restaurant: 'Restaurant', bar: 'Bar', pub: 'Pub', fast_food: 'Fast food',
  bakery: 'Bakery', ice_cream: 'Ice cream', biergarten: 'Beer garden', nightclub: 'Club',
  museum: 'Museum', gallery: 'Gallery', artwork: 'Artwork', theatre: 'Theatre', cinema: 'Cinema',
  attraction: 'Attraction', monument: 'Monument', castle: 'Castle', church: 'Church',
  place_of_worship: 'Place of worship', library: 'Library', viewpoint: 'Viewpoint',
  park: 'Park', garden: 'Garden', beach: 'Beach', peak: 'Peak', nature_reserve: 'Nature reserve',
  marketplace: 'Market', supermarket: 'Supermarket', mall: 'Shopping centre', clothes: 'Shop',
  books: 'Bookshop', spa: 'Spa', hairdresser: 'Hairdresser', hotel: 'Hotel', hostel: 'Hostel',
  guest_house: 'Guest house', apartment: 'Apartment', station: 'Station', bus_station: 'Bus station',
};

/** First guess at the app's category (slugs as seeded in `categories`) from an OSM key/value. */
export function categoryFromOsm(key: string | null | undefined, value: string | null | undefined): string | null {
  const k = key ?? '';
  const v = value ?? '';
  if (k === 'amenity') {
    if (v === 'cafe' || v === 'ice_cream') return 'cafe';
    if (['restaurant', 'fast_food', 'food_court', 'biergarten'].includes(v)) return 'food';
    if (['bar', 'pub', 'nightclub', 'casino'].includes(v)) return 'nightlife';
    if (['theatre', 'cinema', 'arts_centre', 'library', 'place_of_worship'].includes(v)) return 'culture';
    if (v === 'marketplace') return 'shopping';
    if (['bus_station', 'ferry_terminal', 'taxi'].includes(v)) return 'transport';
    return null;
  }
  if (k === 'shop') return v === 'bakery' || v === 'pastry' ? 'cafe' : 'shopping';
  if (k === 'tourism') {
    if (['museum', 'gallery', 'artwork', 'attraction'].includes(v)) return 'culture';
    if (v === 'viewpoint') return 'viewpoint';
    if (['hotel', 'hostel', 'guest_house', 'apartment', 'motel'].includes(v)) return 'accommodation';
    return null;
  }
  if (k === 'historic') return 'culture';
  if (k === 'leisure') {
    if (['park', 'garden', 'nature_reserve', 'beach_resort'].includes(v)) return 'nature';
    if (['spa', 'sauna', 'fitness_centre'].includes(v)) return 'wellness';
    return null;
  }
  if (k === 'natural') return v === 'peak' || v === 'cliff' ? 'viewpoint' : 'nature';
  if (k === 'railway' || k === 'public_transport' || k === 'aeroway') return 'transport';
  return null;
}

function kindLabel(key: string | null | undefined, value: string | null | undefined): string | null {
  if (!value) return null;
  if (KIND_LABEL[value]) return KIND_LABEL[value];
  if (key === 'shop') return 'Shop';
  return null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/** One Photon (GeoJSON) feature → FoundPlace. Features without a name or a point are dropped. */
export function fromPhoton(features: unknown): FoundPlace[] {
  if (!Array.isArray(features)) return [];
  const out: FoundPlace[] = [];
  for (const f of features) {
    const p = (f as { properties?: Record<string, unknown> })?.properties;
    const c = (f as { geometry?: { coordinates?: unknown } })?.geometry?.coordinates;
    if (!p || !Array.isArray(c) || typeof c[0] !== 'number' || typeof c[1] !== 'number') continue;
    const name = str(p.name);
    if (!name) continue;
    const type = str(p.osm_type);
    const osmId = p.osm_id;
    out.push({
      id: type && osmId != null ? `${type}${osmId}` : `${c[1]},${c[0]}`,
      name,
      street: str(p.street),
      number: str(p.housenumber),
      postcode: str(p.postcode),
      city: str(p.city) ?? str(p.town) ?? str(p.village) ?? str(p.district) ?? str(p.county),
      country: str(p.country),
      lat: c[1],
      lng: c[0],
      kind: kindLabel(str(p.osm_key), str(p.osm_value)),
      categoryHint: categoryFromOsm(str(p.osm_key), str(p.osm_value)),
    });
  }
  return out;
}

/** One Nominatim row (with addressdetails=1) → FoundPlace. */
export function fromNominatim(rows: unknown): FoundPlace[] {
  if (!Array.isArray(rows)) return [];
  const out: FoundPlace[] = [];
  for (const r of rows as Record<string, unknown>[]) {
    const lat = Number(r.lat);
    const lng = Number(r.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const a = (r.address ?? {}) as Record<string, unknown>;
    const name = str(r.name) ?? str(String(r.display_name ?? '').split(',')[0]);
    if (!name) continue;
    const type = str(r.osm_type);
    out.push({
      id: type && r.osm_id != null ? `${type[0].toUpperCase()}${r.osm_id}` : `${lat},${lng}`,
      name,
      street: str(a.road) ?? str(a.pedestrian) ?? str(a.footway),
      number: str(a.house_number),
      postcode: str(a.postcode),
      city: str(a.city) ?? str(a.town) ?? str(a.village) ?? str(a.municipality),
      country: str(a.country),
      lat,
      lng,
      kind: kindLabel(str(r.class), str(r.type)),
      categoryHint: categoryFromOsm(str(r.class), str(r.type)),
    });
  }
  return out;
}

/** "Rua Cónego Luciano Afonso 12, 4700 Braga": street and number, then postcode and city. */
export function shortAddress(p: Pick<FoundPlace, 'street' | 'number' | 'postcode' | 'city'>): string {
  const street = [p.street, p.number].filter(Boolean).join(' ');
  const town = [p.postcode, p.city].filter(Boolean).join(' ');
  return [street, town].filter(Boolean).join(', ');
}

/** The same place listed twice (same name and about the same spot) shows once. */
export function dedupePlaces(places: FoundPlace[]): FoundPlace[] {
  const seen = new Set<string>();
  return places.filter((p) => {
    const key = `${p.name.toLowerCase()}|${p.lat.toFixed(3)}|${p.lng.toFixed(3)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Kilometres between two points (haversine), for "0.4 km" on a result. */
export function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Check a place the client sends back (for the suggestion call): right shape, sane sizes. */
export function cleanPlace(p: unknown): FoundPlace | null {
  const o = p as Partial<FoundPlace> | null;
  if (!o || typeof o !== 'object') return null;
  const name = str(o.name);
  if (!name || name.length > 160) return null;
  if (typeof o.lat !== 'number' || typeof o.lng !== 'number') return null;
  if (!(o.lat >= -90 && o.lat <= 90 && o.lng >= -180 && o.lng <= 180)) return null;
  const cut = (v: unknown, n: number) => { const s = str(v); return s ? s.slice(0, n) : null; };
  const id = cut(o.id, 40);
  return {
    id: id && /^[NWR]\d+$/.test(id) ? id : '',
    name,
    street: cut(o.street, 160),
    number: cut(o.number, 20),
    postcode: cut(o.postcode, 20),
    city: cut(o.city, 100),
    country: cut(o.country, 100),
    lat: o.lat,
    lng: o.lng,
    kind: cut(o.kind, 40),
    categoryHint: cut(o.categoryHint, 40),
  };
}
